import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { stripLocaleFromPathname } from "@/config/publicSiteManifest.js";

const SUPPORTED_PUBLIC_LANGS = ["pt", "en", "fr", "es"] as const;
type PublicLocale = (typeof SUPPORTED_PUBLIC_LANGS)[number];
type PublicRouteNamespace =
  | "common"
  | "homepage"
  | "manifesto"
  | "pledge"
  | "urbanismo"
  | "condominios"
  | "pmeToolkit"
  | "privacy"
  | "terms";

type TranslationBundle = Record<string, unknown>;

const publicBundles = import.meta.glob<{ default: TranslationBundle }>(
  "./public/locales/*/*.ts",
);

const loadedBundles = new Set<string>();

const normalizePublicLocale = (locale?: string): PublicLocale => {
  if (locale && SUPPORTED_PUBLIC_LANGS.includes(locale as PublicLocale)) {
    return locale as PublicLocale;
  }

  return "pt";
};

const detectInitialLanguage = (): PublicLocale => {
  if (typeof window === "undefined") {
    return "pt";
  }

  const { locale } = stripLocaleFromPathname(window.location.pathname);

  if (SUPPORTED_PUBLIC_LANGS.includes(locale as PublicLocale)) {
    return locale as PublicLocale;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const langFromUrl = urlParams.get("lang")?.toLowerCase();

  return normalizePublicLocale(langFromUrl);
};

const getBundleKey = (locale: PublicLocale, namespace: PublicRouteNamespace) =>
  `${locale}:${namespace}`;

const getBundleModulePath = (locale: PublicLocale, namespace: PublicRouteNamespace) =>
  `./public/locales/${locale}/${namespace}.ts`;

const loadBundle = async (locale: PublicLocale, namespace: PublicRouteNamespace) => {
  const bundleKey = getBundleKey(locale, namespace);

  if (loadedBundles.has(bundleKey)) {
    return;
  }

  const importer = publicBundles[getBundleModulePath(locale, namespace)];

  if (!importer) {
    throw new Error(`Missing public translation bundle for ${bundleKey}`);
  }

  const module = await importer();
  i18n.addResourceBundle(locale, "translation", module.default, true, true);
  loadedBundles.add(bundleKey);
};

export const ensurePublicTranslations = async (
  locale: string,
  routeNamespace: Exclude<PublicRouteNamespace, "common">,
) => {
  await initPromise;

  const resolvedLocale = normalizePublicLocale(locale);

  await loadBundle(resolvedLocale, "common");
  await loadBundle(resolvedLocale, routeNamespace);

  if (i18n.resolvedLanguage !== resolvedLocale) {
    await i18n.changeLanguage(resolvedLocale);
  }
};

const detectedLanguage = detectInitialLanguage();

const initPromise = i18n.use(initReactI18next).init({
  resources: {},
  lng: detectedLanguage,
  fallbackLng: "pt",
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
  partialBundledLanguages: true,
});

if (typeof document !== "undefined") {
  document.documentElement.lang = detectedLanguage;
}

i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = normalizePublicLocale(lng);
  }
});

export default i18n;
